import {FOOTER_LINKS, usd} from '../imports/site.js'
import {BRAND, FONTS, METALS, CHAIN_LENGTHS, DEFAULTS, LIMITS, PRICES} from '../imports/config.js'
import {loadFont, shapesFromText, shapesFromSvg, normalizeShapes} from './geometry.js'
import {SvgError} from './svg-sanitize.js'
import './pendant-scene.js'
import {initTheme} from './theme.js'
import {reportError, installGlobalReporting} from './report.js'

installGlobalReporting()

/**
 * Typed querySelector; the expected element type comes from the annotated variable.
 * @template {HTMLElement} [T=HTMLElement]
 * @param {string} sel CSS selector
 * @returns {T}
 */
const $ = sel => /** @type {T} */ (document.querySelector(sel))
const params = new URLSearchParams(location.search)

/** @type {import('../types/pendant.js').ConfiguratorState} */
const state = {
  mode: 'word',
  text: DEFAULTS.text,
  font: DEFAULTS.font,
  metal: DEFAULTS.metal,
  chain: DEFAULTS.chain,
  svgName: '',
  svgSource: '',
}
window.__pendant = {state, ready: false}

/** @type {InstanceType<typeof import('./pendant-scene.js').PendantScene>} */
const scene = $('#scene')
scene.still = params.has('still')
scene.bust = !params.has('nobust')

/**
 * Show or clear an error message element.
 * @param {string} id selector of the error element
 * @param {string} [msg] message; empty clears it
 */
function setError(id, msg) {
  const el = $(id)
  el.textContent = msg || ''
  el.hidden = !msg
}

let seq = 0
/** Rebuild the pendant geometry from the current state (latest call wins). */
async function refresh() {
  const mine = ++seq
  window.__pendant.ready = false
  document.body.dataset.ready = 'false'
  try {
    let shapes
    if (state.mode === 'word') {
      const text = state.text.trim()
      if (!text) throw new SvgError('Type a word to see your pendant.')
      const font = await loadFont((FONTS.find(f => f.id === state.font) ?? FONTS[0]).file)
      shapes = shapesFromText(font, text)
      if (!shapes.length) throw new SvgError('None of those characters can be made in this font.')
    } else {
      if (!state.svgSource) return finish(mine)
      shapes = shapesFromSvg(state.svgSource)
    }
    if (mine !== seq) return
    // words keep a sensible letter height (so "I" is not a giant slab); SVGs only get the longest-side limit.
    // On the bust the pendant is smaller so the chain forms a clear V under the neck.
    const [maxW, maxH] = scene.bust ? [118, 50] : [LIMITS.pendantMaxSize, 62]
    const norm = state.mode === 'word' ? normalizeShapes(shapes, maxW, maxH) : normalizeShapes(shapes, maxW)
    setError('#word-error', '')
    setError('#svg-error', '')
    // Lume/solid effects (chain, bust framing) run synchronously inside these setters. A failure there is
    // reported (console + server) and retried once on the next frame (startup races); if it still fails the
    // buyer gets an honest retry state, never a silent blank/half-built preview.
    const apply = () => {
      scene.height = norm.height
      scene.shapes = norm.shapes
    }
    try {
      apply()
    } catch (e) {
      reportError('scene-update', e, `attempt 1 mode=${state.mode} font=${state.font} metal=${state.metal}`)
      await new Promise(r => requestAnimationFrame(r))
      if (mine !== seq) return
      try {
        scene.shapes = []
        apply()
      } catch (e2) {
        reportError('scene-update', e2, `attempt 2 (after retry) mode=${state.mode} font=${state.font}`)
        showFailure()
        return // not ready: checkout stays gated on data-ready
      }
    }
  } catch (e) {
    if (mine !== seq) return
    if (e instanceof SvgError) setError(state.mode === 'word' ? '#word-error' : '#svg-error', e.message)
    else {
      reportError('preview-build', e, `mode=${state.mode} font=${state.font}`)
      showFailure()
      return
    }
  }
  finish(mine)
}

/** Honest failure state: message + "Try again" button (re-runs the build). */
function showFailure() {
  const id = state.mode === 'word' ? '#word-error' : '#svg-error'
  setError(id, "The 3D preview didn't load. ")
  const btn = document.createElement('button')
  btn.type = 'button'
  btn.id = 'retry-preview'
  btn.textContent = 'Try again'
  btn.className = 'link'
  btn.onclick = () => refresh()
  $(id).append(btn)
}

/**
 * Mark the scene ready for tests/checkout once Lume has built meshes (unless superseded).
 * @param {number} mine refresh sequence number
 */
function finish(mine) {
  if (mine !== seq) return
  // wait for lume to build meshes + a few frames
  requestAnimationFrame(() =>
    requestAnimationFrame(() =>
      setTimeout(() => {
        if (mine !== seq) return
        window.__pendant.ready = true
        document.body.dataset.ready = 'true'
      }, 250),
    ),
  )
}

/**
 * @param {string} raw text typed by the buyer
 * @returns {string} error message, or '' when valid
 */
function validateText(raw) {
  if (raw.length > LIMITS.maxChars) return `Up to ${LIMITS.maxChars} characters.`
  if (raw && !LIMITS.allowedChars.test(raw)) return "Letters, numbers, spaces and . - ' & only."
  return ''
}

function build() {
  $('#brand').textContent = BRAND
  $('#footer-links').innerHTML = FOOTER_LINKS.map(l => `<a href="${l.href}">${l.label}</a>`).join('')
  $('#price-note').textContent =
    `${usd(PRICES.word)} USD for any metal or chain length. Made to order; ships in about 10 to 12 business days.`
  const fonts = $('#fonts')
  for (const f of FONTS) {
    const b = document.createElement('button')
    b.type = 'button'
    b.className = 'chip'
    b.dataset.font = f.id
    b.textContent = f.label
    b.style.fontFamily = `'pn-${f.id}'`
    b.setAttribute('aria-pressed', String(f.id === state.font))
    b.onclick = () => {
      state.font = f.id
      fonts.querySelectorAll('.chip').forEach(c => c.setAttribute('aria-pressed', String(c === b)))
      refresh()
    }
    fonts.append(b)
  }
  const faces = FONTS.map(f => `@font-face{font-family:'pn-${f.id}';src:url('${f.file}')}`).join('')
  document.head.append(Object.assign(document.createElement('style'), {textContent: faces}))

  const metals = $('#metals')
  for (const m of METALS) {
    const b = document.createElement('button')
    b.type = 'button'
    b.className = 'swatch'
    b.dataset.metal = m.id
    b.title = m.label
    b.setAttribute('aria-label', m.label)
    b.setAttribute('aria-pressed', String(m.id === state.metal))
    b.innerHTML = `<span class="dot" style="background:${m.swatch}"></span><span class="lbl">${m.label}</span>`
    b.onclick = () => {
      state.metal = m.id
      scene.metal = m.id
      metals.querySelectorAll('.swatch').forEach(c => c.setAttribute('aria-pressed', String(c === b)))
    }
    metals.append(b)
  }
  scene.metal = state.metal

  /** @type {HTMLSelectElement} */
  const chain = $('#chain')
  for (const inch of CHAIN_LENGTHS) chain.append(new Option(`${inch}″`, String(inch), false, inch === state.chain))
  chain.onchange = () => {
    state.chain = Number(chain.value)
    scene.chain = state.chain
  }
  scene.chain = state.chain

  /** @type {HTMLInputElement} */
  const text = $('#text')
  text.value = state.text
  text.maxLength = LIMITS.maxChars + 20 // hard cap so we can flag, not silently truncate
  const hint = () => {
    const long = text.value.length > LIMITS.recommendedChars
    $('#word-hint').hidden = !long
    $('#word-hint').textContent = long
      ? `Tip: the pendant is only about 4×6 mm. ${LIMITS.recommendedChars} characters or fewer reads best.`
      : ''
  }
  text.addEventListener('input', hint)
  hint()
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let t
  text.oninput = () => {
    const msg = validateText(text.value)
    setError('#word-error', msg)
    if (msg) return
    state.text = text.value
    clearTimeout(t)
    t = setTimeout(refresh, 150)
  }
  $('#count').textContent = `0/${LIMITS.maxChars}`
  text.addEventListener('input', () => ($('#count').textContent = `${text.value.length}/${LIMITS.maxChars}`))
  $('#count').textContent = `${text.value.length}/${LIMITS.maxChars}`

  for (const tab of /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll('[role=tab]'))) {
    tab.onclick = () => {
      state.mode = tab.dataset.mode === 'svg' ? 'svg' : 'word'
      document.querySelectorAll('[role=tab]').forEach(x => x.setAttribute('aria-selected', String(x === tab)))
      document
        .querySelectorAll('[role=tabpanel]')
        .forEach(/** @param {any} p */ p => (p.hidden = p.dataset.mode !== state.mode))
      refresh()
    }
  }

  /** @type {HTMLInputElement} */
  const file = $('#svg-file')
  file.onchange = async () => {
    const f = file.files?.[0]
    if (!f) return
    setError('#svg-error', '')
    if (f.size > LIMITS.svgMaxBytes) {
      setError('#svg-error', `File is too large (max ${Math.round(LIMITS.svgMaxBytes / 1024)} KB).`)
      file.value = ''
      return
    }
    state.svgSource = await f.text()
    state.svgName = f.name
    $('#svg-name').textContent = f.name
    const before = scene.shapes
    await refresh()
    if ($('#svg-error').textContent) {
      // keep the previous valid preview; forget the bad file
      state.svgSource = ''
      $('#svg-name').textContent = ''
      scene.shapes = before
      file.value = ''
    }
  }
  $('#svg-clear').onclick = () => {
    state.svgSource = ''
    file.value = ''
    $('#svg-name').textContent = ''
    setError('#svg-error', '')
  }
}

function setupCheckout() {
  /** @type {HTMLButtonElement} */
  const cta = $('#cta')
  const err = $('#checkout-error')
  const price = () => (state.mode === 'svg' ? PRICES.svg : PRICES.word) / 100
  const label = () => `Checkout · $${price()} · free US shipping`
  const sync = () => {
    const ok =
      state.mode === 'word'
        ? !!state.text.trim() && !$('#word-error').textContent
        : !!state.svgSource && !$('#svg-error').textContent
    cta.disabled = !ok || cta.dataset.busy === '1'
    if (cta.dataset.busy !== '1' && cta.textContent !== label()) cta.textContent = label()
  }
  new MutationObserver(sync).observe(document.body, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['hidden', 'aria-selected'],
  })
  $('#text').addEventListener('input', sync)
  sync()
  if (params.has('cancelled')) {
    err.textContent = 'Checkout cancelled. Your design is still here.'
    err.hidden = false
  }
  cta.onclick = async () => {
    err.hidden = true
    cta.dataset.busy = '1'
    cta.disabled = true
    cta.textContent = 'Preparing checkout…'
    try {
      if (!window.__pendant.ready) await new Promise(r => setTimeout(r, 500))
      const body = {
        mode: state.mode,
        text: state.text.trim(),
        font: state.font,
        metal: state.metal,
        chain: state.chain,
        svg: state.mode === 'svg' ? state.svgSource : undefined,
        svgName: state.svgName,
        preview: scene.capturePng(),
      }
      const res = await fetch('/api/checkout', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(body),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.url) throw new Error(data.error || 'Could not start checkout.')
      location.assign(data.url)
    } catch (e) {
      err.textContent = (e instanceof Error ? e.message : '') || 'Could not start checkout.'
      err.hidden = false
      cta.dataset.busy = '0'
      sync()
    }
  }
}

initTheme()
build()
setupCheckout()
refresh()
